/**
 * Store Command Center Data Hook
 *
 * Fetches and manages data for the unified store dashboard.
 * Provides real-time health signals, performance metrics, and inventory summaries.
 *
 * @module StoreCommandCenter/hooks
 */

import { useState, useEffect, useCallback } from 'react';
import { useSocketIO } from '../../../providers/SocketIOProvider';
import config from '../../../config';
import { getValidToken } from '../../../services/tokenRefreshService';
import Logger from '../../../utils/Logger';

// ============================================================================
// Types
// ============================================================================

export interface StoreAddress {
  suburb: string | null;
  city: string | null;
}

export interface WorkingHours {
  open: string;
  close: string;
}

export interface HealthSignals {
  /** Orders dispatched or in tracking */
  inTransit: number;
  /** Orders placed or being picked */
  packing: number;
  /** Orders packed, waiting for driver */
  awaitingPickup: number;
}

export interface Performance {
  /** Average minutes from PLACED to DISPATCHED */
  avgFulfillmentMinutes: number | null;
  /** Average minutes from PACKED to DISPATCHED */
  avgPickupWaitMinutes: number | null;
  /** Orders completed in last 24h */
  ordersCompleted: number;
  /** Placeholder for driver error rate */
  driverErrorRate: number | null;
}

export interface InventorySummary {
  productCount: number;
  totalStock: number;
  lowStockCount: number;
}

export interface StoreCommandData {
  id: string;
  name: string;
  externalId: string;
  isActive: boolean;
  isOpen: boolean;
  /** Paused from the order tablet: closed to customers until the staff resume. */
  isPaused: boolean;
  pausedAt: string | null;
  phoneNumber: string | null;
  /** Shown in the app next to OPEN; null = the store name */
  inAppDisplayName: string | null;
  /** Business name Uber Direct shows its drivers; null = the store name */
  courierDisplayName: string | null;
  address: StoreAddress | null;
  workingHours: WorkingHours | null;
  /** Today's early cutoff time (HH:MM:SS) or null if no cutoff */
  earlyCutoff: string | null;
  /** True if store is open but past today's cutoff time */
  isPastCutoff: boolean;
  /** Whether picking flow is enabled for this store (Diep River HQ feature) */
  enablePickingFlow: boolean;
  /** Last menu sync timestamp (ISO 8601) or null — shows "Ready to Activate" for inactive stores */
  lastMenuSync: string | null;
  signals: HealthSignals;
  performance: Performance;
  inventory: InventorySummary;
}

export interface CommandCenterSummary {
  totalStores: number;
  activeStores: number;
  openStores: number;
  pausedStores: number;
  totalInTransit: number;
  totalPacking: number;
  totalAwaiting: number;
}

export interface CommandCenterData {
  stores: StoreCommandData[];
  summary: CommandCenterSummary;
}

export interface UseCommandCenterReturn {
  data: CommandCenterData | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => Promise<void>;
  selectedStoreId: string | null;
  setSelectedStoreId: (id: string | null) => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Custom hook for fetching Store Command Center data
 *
 * @param autoRefreshInterval - Auto-refresh interval in ms (0 = disabled)
 * @returns Command center data, loading state, and controls
 */
export function useCommandCenter(autoRefreshInterval = 0): UseCommandCenterReturn {
  const [data, setData] = useState<CommandCenterData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);

  /**
   * Fetch command center data from the API
   */
  const fetchData = useCallback(async () => {
    const token = await getValidToken();
    if (!token) {
      setError('Not authenticated');
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      const url = `${config.API_URL}/admin/stores/command-center?includeHidden=true`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch command center data`);
      }

      const result = await response.json();

      setData(result);
      setLastUpdated(new Date());
    } catch (err: any) {
      Logger.error('Failed to fetch command center data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Manual refresh function
   */
  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchData();
  }, [fetchData]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefreshInterval <= 0) return;

    const interval = setInterval(fetchData, autoRefreshInterval);
    return () => clearInterval(interval);
  }, [autoRefreshInterval, fetchData]);

  return {
    data,
    isLoading,
    error,
    lastUpdated,
    refresh,
    selectedStoreId,
    setSelectedStoreId,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format minutes to human-readable time string
 */
export function formatMinutes(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
}

/**
 * Get the total active orders for a store
 */
export function getTotalActiveOrders(signals: HealthSignals): number {
  return signals.inTransit + signals.packing + signals.awaitingPickup;
}

